/**
 * LearnErrorBoundary — last-line-of-defence wrapper around the Learn page.
 *
 * If anything inside the tree throws during render (e.g. a stale lesson
 * payload, a broken iframe child, a corrupted localStorage value that
 * slips past our hygiene step) we catch it here and render a recovery
 * card instead of a blank page.
 *
 * The recovery card has ONE button — "Reset Learn cache" — which wipes
 * the two localStorage keys we own (cc_learn_progress, cc_learn_autoplay_next)
 * and reloads. That undoes 99% of crashes caused by stale browser state.
 */
import { Component } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

const LEARN_STORAGE_KEYS = ['cc_learn_progress', 'cc_learn_autoplay_next'];

class LearnErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface to the console but don't block. Production also gets a
    // structured payload that's easy to grep server-side once we ship
    // client-side error reporting.
    // eslint-disable-next-line no-console
    console.error('[Learn] uncaught render error', error, info?.componentStack);
  }

  reset = () => {
    try {
      LEARN_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
    } catch { /* quota — non-critical */ }
    // Hard reload so any stale React state is dropped.
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="learn-error-boundary">
        <div className="glass-card p-8 max-w-md text-center border-red-500/30">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white mb-2">
            Learn page hit a snag
          </h2>
          <p className="text-[#8a948a] text-sm mb-6">
            We caught the error before it took the page down. Resetting your local Learn cache usually fixes it.
          </p>
          <button
            type="button"
            onClick={this.reset}
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-[#c8b273] hover:bg-[#d8c283] text-black text-xs font-bold uppercase tracking-widest transition-colors"
            data-testid="learn-error-reset"
          >
            <RotateCcw className="w-4 h-4" />
            Reset Learn cache
          </button>
        </div>
      </div>
    );
  }
}

export default LearnErrorBoundary;
